import { useMutation } from '@tanstack/react-query';
import httpClient from '../httpClient';

export interface ExportOptions {
    format?: 'csv' | 'xlsx';
    filters?: Record<string, any>;
    filename?: string;
}

export interface UseExportOptions {
    endpoint: string;
    defaultFilename?: string;
    onSuccess?: () => void;
    onError?: (error: Error) => void;
}

/**
 * Generic export hook that can be used by any entity
 *
 * @param options Configuration options for the export
 * @returns Mutation object for triggering exports
 */
export const useExport = (options: UseExportOptions) => {
    return useMutation<void, Error, ExportOptions>({
        mutationFn: async ({ format = 'csv', filters, filename }) => {
            const response = await httpClient.get(options.endpoint, {
                params: { format, ...filters },
                responseType: 'blob',
                withCredentials: true,
                // Exports can take much longer than the default request timeout on
                // larger datasets; matches the backend's max_execution_time/nginx
                // ceiling (300s, see quantix-api Exportable::exportToCsv) so the client
                // doesn't abort while the server is still legitimately working.
                timeout: 300_000,
            });

            // Create download link
            const blob = new Blob([response.data], { type: 'text/csv' });
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute(
                'download',
                filename || options.defaultFilename || `export.${format}`
            );
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            window.URL.revokeObjectURL(url);
        },
        onSuccess: options.onSuccess,
        onError: options.onError,
    });
};

/**
 * Specific export hooks for common entities
 */

export const useExportLeads = (onSuccess?: () => void, onError?: (error: Error) => void) => {
    return useExport({
        endpoint: '/crm/leads/export',
        defaultFilename: 'leads.csv',
        onSuccess,
        onError,
    });
};

export const useExportCustomers = (onSuccess?: () => void, onError?: (error: Error) => void) => {
    return useExport({
        endpoint: '/crm/customers/export',
        defaultFilename: 'customers.csv',
        onSuccess,
        onError,
    });
};

export const useExportUsers = (onSuccess?: () => void, onError?: (error: Error) => void) => {
    return useExport({
        endpoint: '/crm/users/export',
        defaultFilename: 'users.csv',
        onSuccess,
        onError,
    });
};

export const useExportTransactions = (onSuccess?: () => void, onError?: (error: Error) => void) => {
    return useExport({
        endpoint: '/crm/transactions/export',
        defaultFilename: 'transactions.csv',
        onSuccess,
        onError,
    });
};

export const useExportTradingHistory = (
    onSuccess?: () => void,
    onError?: (error: Error) => void
) => {
    return useExport({
        endpoint: '/crm/trading-history/export',
        defaultFilename: 'trading-history.csv',
        onSuccess,
        onError,
    });
};

export const useExportInstruments = (onSuccess?: () => void, onError?: (error: Error) => void) => {
    return useExport({
        endpoint: '/crm/instruments/export',
        defaultFilename: 'instruments.csv',
        onSuccess,
        onError,
    });
};

export const useExportKycRequests = (onSuccess?: () => void, onError?: (error: Error) => void) => {
    return useExport({
        endpoint: '/crm/kyc/export',
        defaultFilename: 'kyc-requests.csv',
        onSuccess,
        onError,
    });
};

export const useExportTradingAccounts = (
    onSuccess?: () => void,
    onError?: (error: Error) => void
) => {
    return useExport({
        endpoint: '/crm/trading-accounts/export',
        defaultFilename: 'trading-accounts.csv',
        onSuccess,
        onError,
    });
};

export const useExportAuditLogs = (onSuccess?: () => void, onError?: (error: Error) => void) => {
    return useExport({
        endpoint: '/crm/audit-logs/export',
        defaultFilename: 'audit-logs.csv',
        onSuccess,
        onError,
    });
};
